import { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

import { draftContentToHtml, htmlContentToDraft } from '../../../../../utils/text-editor-util';

import Modal from '../../../../common/modal/Modal';
import RichTextEditor from '../../../../common/inputs/rich-text-editor/RichTextEditor';
import FilePicker from '../../../../common/inputs/file-picker/FilerPicker';
import Button from '../../../../common/button/Button';

import { ReactComponent as FileIcon } from '../../../../../resources/icons/file.svg';
import { ReactComponent as UploadIcon } from '../../../../../resources/icons/upload.svg';
import { ReactComponent as LinkIcon } from '../../../../../resources/icons/link.svg';
import { ReactComponent as PastIcon } from '../../../../../resources/icons/paste.svg';

import './tutorial-modal.scss';

const TutorialModal = ({ show, onClose, tutorial, createTutorial, updateTutorial }) => {
  const [editorState, setEditorState] = useState(undefined);
  const [file, setFile] = useState(undefined);
  const [link, setLink] = useState('');

  const fileRef = useRef(null);

  useEffect(() => {
    if (tutorial) {
      const { tutorialText, tutorialDocumentVO, videoURLString } = tutorial;
      setEditorState(htmlContentToDraft(tutorialText));
      setFile(tutorialDocumentVO.name ? tutorialDocumentVO : undefined);
      setLink(videoURLString ? videoURLString : '');
    }
  }, [tutorial]);

  const closeModal = () => {
    setEditorState(undefined);
    setFile(undefined);
    setLink('');
    onClose();
  };

  const handleFileChange = ({ target }) => {
    setFile(target.files[0]);
    setLink('');
  };

  const handleLinkChange = ({ target }) => {
    setLink(target.value);
    setFile(undefined);
  };

  const handlePasteClick = async () => {
    setFile(undefined);
    if (navigator.clipboard.readText) {
      const clipboardContents = await navigator.clipboard.readText();
      setLink(clipboardContents ? clipboardContents : '');
    }
  };

  const handleSaveClick = () => {
    let tutorialToSave = {
      text: draftContentToHtml(editorState),
      file,
      link,
    };

    if (!tutorial) {
      createTutorial(tutorialToSave, closeModal);
    } else {
      tutorialToSave = {
        ...tutorialToSave,
        externalReference: tutorial.externalReference,
      };
      updateTutorial(tutorialToSave, closeModal);
    }
  };

  return (
    <Modal show={show} onClose={closeModal}>
      <section className="tutorial-modal">
        <h1 className="tutorial-modal__header">{tutorial ? 'Update' : 'New'} Tutorial</h1>

        <section className="tutorial-modal__content">
          <section className="tutorial-modal__editor">
            <RichTextEditor editorState={editorState} setEditorState={setEditorState} />
          </section>

          <section className="tutorial-modal__file-upload">
            <FileIcon />
            <p className="tutorial-modal__file-upload__text">
              {file ? file.name : '(Optional) Click button to upload'}
            </p>
            <FilePicker
              fileRef={fileRef}
              enableFileLimit={true}
              onChange={handleFileChange}
              triggerElement={
                <button className="tutorial-modal__file-upload__button" onClick={() => fileRef.current.click()}>
                  <UploadIcon />
                </button>
              }
            />
          </section>

          <p className="tutorial-modal__or">Or</p>

          <section className="tutorial-modal__link-input">
            <LinkIcon />
            <input
              className="tutorial-modal__link-input__input"
              type="text"
              placeholder="(Optional) Paste/type link here"
              value={link}
              onChange={handleLinkChange}
            />
            {navigator.clipboard.readText && (
              <button className="tutorial-modal__link-input__button" onClick={handlePasteClick}>
                <PastIcon />
              </button>
            )}
          </section>

          <section className="tutorial-modal__footer">
            <Button onClick={closeModal} secondary>
              Cancel
            </Button>
            <Button onClick={handleSaveClick}>Save</Button>
          </section>
        </section>
      </section>
    </Modal>
  );
};

TutorialModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  tutorial: PropTypes.shape({}),
  createTutorial: PropTypes.func.isRequired,
  updateTutorial: PropTypes.func.isRequired,
};

TutorialModal.defaultProps = {
  tutorial: undefined,
};

export default TutorialModal;
